/**
 * `<script>` id used for the Jornaya (LeadiD) campaign script. Kept identical to
 * the legacy `<Script id>` so `next/script` dedupes later mounts (e.g. TYP
 * components) against the copy the provider loaded.
 */
export const JORNAYA_SCRIPT_ID = 'LeadiDscript_campaign';

/** Jornaya campaign script; it calls `window.onGetLeadId(token)` once a token exists. */
export const JORNAYA_SCRIPT_URL =
  'https://create.lidstatic.com/campaign/d41d6781-7d2b-3af5-e3af-d0ebfe85d74f.js?snippet_version=2&callback=onGetLeadId';

/**
 * Jornaya `src` for a 1-based load attempt. The first attempt uses the exact
 * legacy URL; retries add a cache-buster because `next/script` caches by `src`.
 */
export const buildJornayaSrc = (attempt: number): string =>
  attempt === 1
    ? JORNAYA_SCRIPT_URL
    : `${JORNAYA_SCRIPT_URL}&hc_retry=${attempt}`;

/** `<script>` id used for the TrustedForm script (same as the legacy plates). */
export const TRUSTED_FORM_SCRIPT_ID = 'TrustedFormScript';

/** Hidden form field TrustedForm fills with the certificate URL. */
export const TRUSTED_FORM_FIELD = 'xxTrustedFormCertUrl';

/** Hidden form field that carries the Jornaya token on submit. */
export const LEAD_ID_FIELD = 'universal_leadid';

/**
 * TrustedForm `src`. Every call returns a new URL (the `l` cache-buster the
 * legacy plates already use), which also makes each retry a fresh fetch.
 */
export const buildTrustedFormSrc = (): string =>
  `https://api.trustedform.com/trustedform.js?provide_referrer=false&field=${TRUSTED_FORM_FIELD}&invert_field_sensitivity=false&l=${Date.now().toString()}${Math.random().toString()}`;

/**
 * How long (ms) to wait for `onGetLeadId` after the Jornaya script is loaded
 * and initialised. When it elapses, `LeadiD.reInit()` is called once and the
 * same window is waited again before giving up with `no-token`. Longer than
 * the PII plate's 3 s because the user is usually still several plates away.
 */
export const JORNAYA_TOKEN_WAIT_MS = 5_000;

/** How often (ms) to look for the TrustedForm certificate input. */
export const TRUSTED_FORM_POLL_MS = 500;

/**
 * A Jornaya token is a UUID. Values from `window.onGetLeadId` (any script can
 * call it) that don't match are ignored.
 */
export const isJornayaToken = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/**
 * A TrustedForm certificate URL is served over HTTPS from
 * `cert.trustedform.com`. The provider scrapes it from the DOM across the whole
 * survey, so anything else (a CMS block or third-party tag planting a field
 * with the same name) is ignored instead of becoming the lead's consent proof.
 */
export const isTrustedFormCertUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'cert.trustedform.com';
  } catch {
    return false;
  }
};
